import IndexSkusCarouselSkeleton from '@/components/common/Skeleton/IndexSkusCarouselSkeleton';
import { memo, useRef, useState, useEffect, useCallback } from 'react';
import { Navigation, Virtual } from 'swiper/modules';
import { Swiper, SwiperProps, SwiperSlide } from 'swiper/react';
import IndexSkuCard from '@/components/home/Index/IndexSkuCard';
import { SkuDTO } from '@/types/api/sku';
import 'swiper/css/navigation';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { NavigationOptions, Swiper as SwiperType } from 'swiper/types';

export type SkusCommonCarouselProps = {
  skus: SkuDTO[];
  isLoading?: boolean;
} & Omit<SwiperProps, 'children'>;

function SkusCommonCarousel({ skus, isLoading, ...swiperOptions }: SkusCommonCarouselProps) {
  const prevRef = useRef<HTMLButtonElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);
  const [swiper, setSwiper] = useState<SwiperType | null>(null);

  const defaultSwiperConfig = {
    ...swiperOptions,
    modules: [Virtual, Navigation, ...(swiperOptions?.modules || [])],
    breakpoints: {
      0: {
        slidesPerView: 1,
        spaceBetween: 8,
      },
      350: {
        slidesPerView: 1,
        spaceBetween: 8,
      },
      430: {
        slidesPerView: 2,
        spaceBetween: 8,
      },
      716: {
        slidesPerView: 3,
        spaceBetween: 10,
      },
      886: {
        slidesPerView: 3,
        spaceBetween: 10,
      },
      1024: {
        slidesPerView: 3,
        spaceBetween: 20,
      },
      ...swiperOptions.breakpoints,
    },
    loop: false,
    className:
      '!tw-h-fit max-nine:tw-h-[300px] tw-relative !tw-overflow-x-clip !tw-overflow-y-visible',
    navigation: {
      prevEl: null,
      nextEl: null,
    },
    onSlideChange: (swiper: SwiperType) => {
      setIsBeginning(swiper.isBeginning);
      setIsEnd(swiper.isEnd);
    },
  };

  // Update navigation when refs or swiper change
  const updateNavigation = useCallback(() => {
    if (!swiper || !prevRef.current || !nextRef.current) return;

    // Only update if navigation is available
    if (swiper.navigation && typeof swiper.navigation.update === 'function') {
      // Set navigation elements
      swiper.params.navigation = {
        prevEl: prevRef.current,
        nextEl: nextRef.current,
      } as NavigationOptions;

      // Update swiper navigation
      swiper.navigation.update();
    }
  }, [swiper]);

  // Call updateNavigation when swiper or refs change
  useEffect(() => {
    if (swiper) {
      updateNavigation();
    }
  }, [swiper, updateNavigation]);

  if (isLoading) {
    return <IndexSkusCarouselSkeleton />;
  }

  return (
    <div className="tw-relative tw-w-full max-nine:tw-pl-4">
      <Swiper
        {...defaultSwiperConfig}
        onSwiper={swiperInstance => {
          setSwiper(swiperInstance);
        }}
        onAfterInit={swiperInstance => {
          // Set initial states
          setIsBeginning(swiperInstance.isBeginning);
          setIsEnd(swiperInstance.isEnd);
        }}
      >
        {skus.map((sku, index) => (
          <SwiperSlide
            key={sku.id}
            virtualIndex={index}
            className="!tw-h-[460px] tw-max-w-[45vw] tw-pr-0.5 max-nine:!tw-h-auto"
          >
            <IndexSkuCard sku={sku} />
          </SwiperSlide>
        ))}
      </Swiper>

      {!isBeginning && (
        <button
          ref={prevRef}
          onClick={() => swiper?.slidePrev()}
          className="transition-all duration-300 tw-absolute tw-left-3 tw-top-1/2 tw-z-10 tw-flex tw-h-11 tw-w-11 -tw-translate-y-1/2 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-border-gray-300/40 tw-bg-white/70 tw-text-main tw-shadow-lg tw-backdrop-blur-sm tw-transition-colors hover:tw-bg-main hover:tw-text-white focus-visible:tw-ring-2 focus-visible:tw-ring-main/80 max-nine:tw-hidden"
        >
          <ChevronLeft className="tw-h-5 tw-w-5" />
        </button>
      )}

      {!isEnd && (
        <button
          ref={nextRef}
          onClick={() => swiper?.slideNext()}
          className="transition-all duration-300 tw-absolute tw-right-3 tw-top-1/2 tw-z-10 tw-flex tw-h-11 tw-w-11 -tw-translate-y-1/2 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-border-gray-300/40 tw-bg-white/70 tw-text-main tw-shadow-lg tw-backdrop-blur-sm tw-transition-colors hover:tw-bg-main hover:tw-text-white focus-visible:tw-ring-2 focus-visible:tw-ring-main/80 max-nine:tw-hidden"
        >
          <ChevronRight className="tw-h-5 tw-w-5" />
        </button>
      )}
    </div>
  );
}

export default memo(SkusCommonCarousel);
